/**
 * Account vault: envelope encryption of the data key.
 *
 * A random 256-bit data key (DEK) encrypts every record. The DEK itself is
 * wrapped twice — once under a key derived from the password, once under a key
 * derived from the recovery code. That indirection buys two things: changing
 * the password only re-wraps 32 bytes instead of re-encrypting the whole
 * database, and losing the password does not lose the data provided the
 * recovery code was written down.
 *
 * There is no server. Nothing can reset a password. If both the password and
 * the recovery code are gone, the data is gone — which is the honest cost of
 * the confidentiality requirement in the source concept.
 */

import {
  deriveKeyMaterial,
  fromBase64,
  generateDataKey,
  importAesKey,
  open,
  randomBytes,
  seal,
  sha256Hex,
  timingSafeEqual,
  toBase64,
  type Sealed,
} from './primitives';
import { accountDbName } from '@/db/schema';

/**
 * Crockford base32 without I, L, O and U — the characters people misread when
 * copying a code off a screen onto paper.
 */
const RECOVERY_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const RECOVERY_GROUPS = 6;
const RECOVERY_GROUP_LENGTH = 5;

export interface AccountRecord {
  id: string;
  username: string;
  displayName: string;
  driverCode: string;
  createdAt: string;

  /**
   * The IndexedDB database holding this driver's books.
   *
   * Stored rather than derived because the first account on an install
   * predating per-account databases keeps the original `fahrtenbuch-pro`, and
   * moving its data purely to satisfy a naming rule would be a needless risk.
   */
  dbName: string;

  /** Salt for the password-derived key-encryption key. */
  passwordSalt: string;
  /** Salt for the recovery-code-derived key-encryption key. */
  recoverySalt: string;

  /** DEK wrapped under the password KEK. */
  wrappedKeyPassword: Sealed;
  /** DEK wrapped under the recovery KEK. */
  wrappedKeyRecovery: Sealed;

  /**
   * SHA-256 of the password KEK, so a wrong password is rejected before an
   * AES-GCM authentication failure has to be interpreted.
   */
  passwordVerifier: string;

  kdf: {
    algorithm: 'argon2id';
    memoryKib: number;
    iterations: number;
    parallelism: number;
  };
}

export interface UnlockedVault {
  account: AccountRecord;
  /** Non-extractable AES-GCM key. Lives in memory only. */
  dataKey: CryptoKey;
}

export function generateRecoveryCode(): string {
  const groups: string[] = [];
  for (let g = 0; g < RECOVERY_GROUPS; g += 1) {
    const bytes = randomBytes(RECOVERY_GROUP_LENGTH);
    let group = '';
    for (let i = 0; i < RECOVERY_GROUP_LENGTH; i += 1) {
      group += RECOVERY_ALPHABET[bytes[i]! % RECOVERY_ALPHABET.length];
    }
    groups.push(group);
  }
  return groups.join('-');
}

/** Strip formatting and fold the characters people commonly mistype. */
export function normaliseRecoveryCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    .replace(/U/g, 'V');
}

async function deriveKek(secret: string, saltB64: string): Promise<{ raw: Uint8Array; key: CryptoKey }> {
  const raw = await deriveKeyMaterial(secret, fromBase64(saltB64));
  return { raw, key: await importAesKey(raw) };
}

export interface CreatedAccount {
  account: AccountRecord;
  dataKey: CryptoKey;
  recoveryCode: string;
}

export async function createAccount(params: {
  username: string;
  displayName: string;
  driverCode: string;
  password: string;
}): Promise<CreatedAccount> {
  const passwordSalt = toBase64(randomBytes(16));
  const recoverySalt = toBase64(randomBytes(16));
  const recoveryCode = generateRecoveryCode();

  const passwordKek = await deriveKek(params.password, passwordSalt);
  const recoveryKek = await deriveKek(normaliseRecoveryCode(recoveryCode), recoverySalt);

  const dek = await generateDataKey();
  const rawDek = new Uint8Array(await crypto.subtle.exportKey('raw', dek));

  const id = crypto.randomUUID();
  const account: AccountRecord = {
    id,
    username: params.username.trim().toLowerCase(),
    displayName: params.displayName.trim(),
    driverCode: params.driverCode.trim(),
    createdAt: new Date().toISOString(),
    dbName: accountDbName(id),
    passwordSalt,
    recoverySalt,
    wrappedKeyPassword: await seal(passwordKek.key, rawDek),
    wrappedKeyRecovery: await seal(recoveryKek.key, rawDek),
    passwordVerifier: await sha256Hex(passwordKek.raw),
    kdf: {
      algorithm: 'argon2id',
      memoryKib: 65_536,
      iterations: 3,
      parallelism: 1,
    },
  };

  // Re-import non-extractable for runtime use; the extractable handle above
  // existed only long enough to wrap the key.
  const dataKey = await importAesKey(rawDek);
  rawDek.fill(0);

  return { account, dataKey, recoveryCode };
}

export class WrongPasswordError extends Error {
  constructor() {
    super('Passwort falsch.');
    this.name = 'WrongPasswordError';
  }
}

export class WrongRecoveryCodeError extends Error {
  constructor() {
    super('Wiederherstellungscode falsch.');
    this.name = 'WrongRecoveryCodeError';
  }
}

export async function unlockWithPassword(
  account: AccountRecord,
  password: string,
): Promise<UnlockedVault> {
  const kek = await deriveKek(password, account.passwordSalt);
  const verifier = await sha256Hex(kek.raw);
  if (!timingSafeEqual(verifier, account.passwordVerifier)) {
    throw new WrongPasswordError();
  }
  const rawDek = await open(kek.key, account.wrappedKeyPassword);
  const dataKey = await importAesKey(rawDek);
  rawDek.fill(0);
  return { account, dataKey };
}

export async function unlockWithRecoveryCode(
  account: AccountRecord,
  recoveryCode: string,
): Promise<UnlockedVault> {
  const kek = await deriveKek(normaliseRecoveryCode(recoveryCode), account.recoverySalt);
  let rawDek: Uint8Array;
  try {
    rawDek = await open(kek.key, account.wrappedKeyRecovery);
  } catch {
    throw new WrongRecoveryCodeError();
  }
  const dataKey = await importAesKey(rawDek);
  rawDek.fill(0);
  return { account, dataKey };
}

/**
 * Re-wrap the DEK under a new password. The data is untouched, so this is
 * fast regardless of how many years of trips are stored.
 */
export async function changePassword(
  account: AccountRecord,
  currentPassword: string,
  newPassword: string,
): Promise<AccountRecord> {
  const currentKek = await deriveKek(currentPassword, account.passwordSalt);
  if (!timingSafeEqual(await sha256Hex(currentKek.raw), account.passwordVerifier)) {
    throw new WrongPasswordError();
  }
  const rawDek = await open(currentKek.key, account.wrappedKeyPassword);

  const passwordSalt = toBase64(randomBytes(16));
  const newKek = await deriveKek(newPassword, passwordSalt);
  const wrappedKeyPassword = await seal(newKek.key, rawDek);
  rawDek.fill(0);

  return {
    ...account,
    passwordSalt,
    wrappedKeyPassword,
    passwordVerifier: await sha256Hex(newKek.raw),
  };
}

/**
 * Set a new password using the recovery code, and issue a fresh recovery code
 * so the old slip of paper stops being a credential.
 */
export async function resetPasswordWithRecoveryCode(
  account: AccountRecord,
  recoveryCode: string,
  newPassword: string,
): Promise<{ account: AccountRecord; recoveryCode: string; dataKey: CryptoKey }> {
  const oldKek = await deriveKek(normaliseRecoveryCode(recoveryCode), account.recoverySalt);
  let rawDek: Uint8Array;
  try {
    rawDek = await open(oldKek.key, account.wrappedKeyRecovery);
  } catch {
    throw new WrongRecoveryCodeError();
  }

  const passwordSalt = toBase64(randomBytes(16));
  const recoverySalt = toBase64(randomBytes(16));
  const newRecoveryCode = generateRecoveryCode();

  const passwordKek = await deriveKek(newPassword, passwordSalt);
  const recoveryKek = await deriveKek(normaliseRecoveryCode(newRecoveryCode), recoverySalt);

  const next: AccountRecord = {
    ...account,
    passwordSalt,
    recoverySalt,
    wrappedKeyPassword: await seal(passwordKek.key, rawDek),
    wrappedKeyRecovery: await seal(recoveryKek.key, rawDek),
    passwordVerifier: await sha256Hex(passwordKek.raw),
  };

  const dataKey = await importAesKey(rawDek);
  rawDek.fill(0);
  return { account: next, recoveryCode: newRecoveryCode, dataKey };
}

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  issues: string[];
}

/**
 * Deliberately simple: length dominates, and we say why rather than showing a
 * bar. There is no password reset here, so the failure mode of a weak password
 * is theft, and the failure mode of a forgotten one is total loss.
 */
export function assessPassword(password: string): PasswordStrength {
  const issues: string[] = [];
  if (password.length < 12) issues.push('Mindestens 12 Zeichen verwenden.');
  if (!/[a-zäöüß]/.test(password)) issues.push('Kleinbuchstaben ergänzen.');
  if (!/[A-ZÄÖÜ]/.test(password)) issues.push('Großbuchstaben ergänzen.');
  if (!/\d/.test(password)) issues.push('Ziffern ergänzen.');

  let score = 0;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (/[a-zäöüß]/.test(password) && /[A-ZÄÖÜ]/.test(password)) score += 1;
  if (/\d/.test(password) && /[^\w\s]/.test(password)) score += 1;

  const labels = ['sehr schwach', 'schwach', 'brauchbar', 'gut', 'stark'];
  const clamped = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  return { score: clamped, label: labels[clamped]!, issues };
}
